import { addToolBreadcrumb } from '@tunasong/models'
import {
  computeCofSessionStats,
  generateCofQuizQuestion,
  validateCofQuizAnswer,
} from '@tunasong/music-lib'
import type {
  CofQuizPicked,
  CofQuizQuestion,
  CofQuizQuestionKind,
  CofQuizResult,
  CofQuizSubMode,
  CofQuizVerdict,
  KeySlot,
} from '@tunasong/music-lib'
import { useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useEffectEvent, useState, type FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { CofExplorePanel } from './cof-explore-panel.js'
import { CofQuizFeedback } from './cof-quiz-feedback.js'
import { CofQuizQuestionPanel } from './cof-quiz-question.js'
import { CofWheelSummary } from './cof-wheel-summary.js'
import { CofWheelWelcome } from './cof-wheel-welcome.js'
import { parseCofKeyParam, parseCofQuizSubMode, parseCofSubMode } from './cof-wheel-params.js'
import { useCofKeyAudition } from './cof-wheel-playback.js'
import type { CofSubMode } from './cof-wheel-params.js'
import styles from './cof-wheel.module.css'

const QUESTIONS_PER_SESSION = 10
const AUTOPLAY_DELAY_MS = 250

type SessionState =
  | {
      phase: 'welcome'
      preSubMode: CofSubMode | null
      preQuizSubMode: CofQuizSubMode | null
      preKey: KeySlot | null
    }
  | { phase: 'exploring'; key: { name: string; ring: 'major' | 'minor' } }
  | {
      phase: 'asking-quiz'
      index: number
      subMode: CofQuizSubMode
      history: CofQuizQuestionKind[]
      question: CofQuizQuestion
      results: CofQuizResult[]
    }
  | {
      phase: 'feedback-quiz'
      index: number
      subMode: CofQuizSubMode
      history: CofQuizQuestionKind[]
      question: CofQuizQuestion
      verdict: CofQuizVerdict
      results: CofQuizResult[]
    }
  | { phase: 'done-quiz'; subMode: CofQuizSubMode; results: CofQuizResult[] }

/**
 * CoF Wheel v1 host. State machine: welcome → exploring | asking-quiz →
 * feedback-quiz → done-quiz. URL params `?mode=` and `?key=` pre-select at
 * initial mount.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §7
 */
export const CofWheel: FC = () => {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<SessionState>(() => {
    const preSubMode = parseCofSubMode(searchParams.get('mode'))
    const preQuizSubMode = parseCofQuizSubMode(searchParams.get('submode'))
    const preKey = parseCofKeyParam(searchParams.get('key'))
    if (preSubMode === 'quiz') {
      const subMode = preQuizSubMode ?? 'mixed'
      return {
        phase: 'asking-quiz',
        index: 0,
        subMode,
        history: [],
        question: generateCofQuizQuestion(subMode, Math.random, []),
        results: [],
      }
    }
    if (preSubMode === 'explore') {
      const name = preKey ? preKey.canonical : 'C'
      return { phase: 'exploring', key: { name, ring: 'major' } }
    }
    return { phase: 'welcome', preSubMode, preQuizSubMode, preKey }
  })

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'cof-wheel' })
  }, [])

  // Active key for audition is derived from the phase.
  const auditionKey =
    state.phase === 'exploring'
      ? state.key
      : state.phase === 'asking-quiz' || state.phase === 'feedback-quiz'
        ? state.question.referenceKey
        : null
  const { play } = useCofKeyAudition({ key: auditionKey })

  // Autoplay audition on every exploring + asking-quiz entry. Skip feedback
  // (user-driven replay only). The trigger key includes the phase so a
  // welcome → exploring transition with the same key still fires.
  const auditionTrigger =
    auditionKey && state.phase !== 'feedback-quiz'
      ? `${auditionKey.name}-${auditionKey.ring}-${state.phase}`
      : null
  const firePlay = useEffectEvent(() => play())
  useEffect(() => {
    if (auditionTrigger === null) {
      return
    }
    const handle = window.setTimeout(() => firePlay(), AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [auditionTrigger])

  const handleStartExplore = (key: { name: string; ring: 'major' | 'minor' }): void => {
    addToolBreadcrumb('opened', { tool: 'cof-wheel', mode: 'explore', key: key.name })
    setState({ phase: 'exploring', key })
  }

  const handleStartQuiz = (subMode: CofQuizSubMode): void => {
    addToolBreadcrumb('opened', { tool: 'cof-wheel', mode: 'quiz', quizSubMode: subMode })
    setState({
      phase: 'asking-quiz',
      index: 0,
      subMode,
      history: [],
      question: generateCofQuizQuestion(subMode, Math.random, []),
      results: [],
    })
  }

  const handleSelectKey = (key: { name: string; ring: 'major' | 'minor' }): void => {
    if (state.phase !== 'exploring') {
      return
    }
    addToolBreadcrumb('opened', { tool: 'cof-wheel', mode: 'explore', key: key.name })
    setState({ phase: 'exploring', key })
  }

  const handleAnswer = (picked: CofQuizPicked): void => {
    if (state.phase !== 'asking-quiz') {
      return
    }
    const verdict = validateCofQuizAnswer(state.question, picked)
    addToolBreadcrumb('answered', {
      tool: 'cof-wheel',
      subMode: 'quiz',
      quizSubMode: state.subMode,
      kind: state.question.kind,
      correct: verdict.correct,
    })
    setState({
      phase: 'feedback-quiz',
      index: state.index,
      subMode: state.subMode,
      history: state.history,
      question: state.question,
      verdict,
      results: state.results,
    })
  }

  const handleNext = (): void => {
    if (state.phase !== 'feedback-quiz') {
      return
    }
    const results: CofQuizResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    const nextHistory: CofQuizQuestionKind[] = [...state.history, state.question.kind]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-quiz', subMode: state.subMode, results })
      return
    }
    setState({
      phase: 'asking-quiz',
      index: state.index + 1,
      subMode: state.subMode,
      history: nextHistory,
      question: generateCofQuizQuestion(state.subMode, Math.random, nextHistory),
      results,
    })
  }

  const handleRestart = (): void => {
    if (state.phase !== 'done-quiz') {
      return
    }
    setState({
      phase: 'asking-quiz',
      index: 0,
      subMode: state.subMode,
      history: [],
      question: generateCofQuizQuestion(state.subMode, Math.random, []),
      results: [],
    })
  }

  const handleSwitchToExplore = (): void => {
    setState({ phase: 'exploring', key: { name: 'C', ring: 'major' } })
  }

  const footerTools: RelatedTool[] = [
    { targetRoute: '/app/modes', lexiconId: 'crosslink-cof-to-mode' },
    { targetRoute: '/app/quiz', lexiconId: 'crosslink-cof-to-quiz' },
    { targetRoute: '/music-theory', lexiconId: 'crosslink-cof-to-suite' },
  ]

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <CofWheelWelcome
          preSelectedSubMode={state.preSubMode}
          preSelectedKey={state.preKey}
          onStartExplore={handleStartExplore}
          onStartQuiz={handleStartQuiz}
        />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'exploring') {
    return (
      <div className={styles.container}>
        <CofExplorePanel selectedKey={state.key} onSelectKey={handleSelectKey} onPlay={play} />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'asking-quiz') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <CofQuizQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive
          onHearIt={play}
          onAnswer={handleAnswer}
        />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'feedback-quiz') {
    const scoreCorrect =
      state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <CofQuizQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={play}
          onAnswer={() => undefined}
        />
        <CofQuizFeedback
          question={state.question}
          verdict={state.verdict}
          onHearAgain={play}
          onNext={handleNext}
        />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  // state.phase === 'done-quiz'
  return (
    <div className={styles.container}>
      <CofWheelSummary
        stats={computeCofSessionStats(state.results)}
        onRestart={handleRestart}
        onSwitchToExplore={handleSwitchToExplore}
      />
      <RelatedTools tools={footerTools} />
    </div>
  )
}
